// src/components/wholesaler/Profile.js
import { useState } from 'react';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../config/firebase';
import { Building, MapPin, FileText, Save, X, Loader2, CheckCircle } from 'lucide-react';
import { DEFAULT_PRICING_TIERS } from '../../config/constants';

export function Profile({ userData, userId }) {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [formData, setFormData] = useState({
    companyName: userData?.companyName || '',
    contactName: userData?.contactName || '',
    phone: userData?.phone || '',
    website: userData?.website || '',
    shippingStreet: userData?.shippingAddress?.street || '',
    shippingCity: userData?.shippingAddress?.city || '',
    shippingState: userData?.shippingAddress?.state || '',
    shippingZip: userData?.shippingAddress?.zip || '',
    billingStreet: userData?.billingAddress?.street || '',
    billingCity: userData?.billingAddress?.city || '',
    billingState: userData?.billingAddress?.state || '',
    billingZip: userData?.billingAddress?.zip || ''
  });

  const tierInfo = DEFAULT_PRICING_TIERS[userData?.pricingTier || 'standard'];

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await updateDoc(doc(db, 'users', userId), {
        companyName: formData.companyName,
        contactName: formData.contactName,
        phone: formData.phone,
        website: formData.website,
        shippingAddress: {
          street: formData.shippingStreet,
          city: formData.shippingCity,
          state: formData.shippingState,
          zip: formData.shippingZip
        },
        billingAddress: formData.billingStreet ? {
          street: formData.billingStreet,
          city: formData.billingCity,
          state: formData.billingState,
          zip: formData.billingZip
        } : null,
        updatedAt: Timestamp.now()
      });

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      setMessage({ type: 'error', text: 'Failed to update profile. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleW9Upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setMessage({ type: 'error', text: 'Please upload a PDF file.' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'File size must be less than 5MB.' });
      return;
    }

    setUploading(true);
    setMessage({ type: '', text: '' });

    try {
      const storageRef = ref(storage, `w9/${userId}/${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);

      await updateDoc(doc(db, 'users', userId), {
        w9Url: downloadUrl,
        w9UploadedAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      setMessage({ type: 'success', text: 'W9 uploaded successfully!' });
    } catch (err) {
      console.error('Error uploading W9:', err);
      setMessage({ type: 'error', text: 'Failed to upload W9. Please try again.' });
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      companyName: userData?.companyName || '',
      contactName: userData?.contactName || '',
      phone: userData?.phone || '',
      website: userData?.website || '',
      shippingStreet: userData?.shippingAddress?.street || '',
      shippingCity: userData?.shippingAddress?.city || '',
      shippingState: userData?.shippingAddress?.state || '',
      shippingZip: userData?.shippingAddress?.zip || '',
      billingStreet: userData?.billingAddress?.street || '',
      billingCity: userData?.billingAddress?.city || '',
      billingState: userData?.billingAddress?.state || '',
      billingZip: userData?.billingAddress?.zip || ''
    });
    setIsEditing(false);
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">My Profile</h2>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800"
          >
            Edit Profile
          </button>
        )}
      </div>

      {message.text && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-700' 
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.text}
        </div>
      )}

      {/* Pricing Tier Badge */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-700 text-white rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-300">Your Pricing Tier</p>
            <h3 className="text-2xl font-bold">{tierInfo?.name || 'Standard'}</h3>
          </div>
          {userData?.tierDiscount > 0 && (
            <div className="text-right">
              <p className="text-sm text-gray-300">Your Discount</p>
              <p className="text-2xl font-bold text-green-400">{userData.tierDiscount}% OFF</p>
            </div>
          )}
        </div>
      </div>

      {/* Company Information */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center gap-2 mb-4 pb-2 border-b">
          <Building size={20} />
          <h3 className="text-lg font-semibold">Company Information</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
            {isEditing ? (
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
              />
            ) : (
              <p className="text-gray-900">{userData?.companyName || '-'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
            {isEditing ? (
              <input
                type="text"
                value={formData.contactName}
                onChange={(e) => setFormData({...formData, contactName: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
              />
            ) : (
              <p className="text-gray-900">{userData?.contactName || '-'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <p className="text-gray-900">{userData?.email || '-'}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            {isEditing ? (
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
              />
            ) : (
              <p className="text-gray-900">{userData?.phone || '-'}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
            {isEditing ? (
              <input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({...formData, website: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
                placeholder="https://"
              />
            ) : (
              <p className="text-gray-900">
                {userData?.website ? (
                  <a href={userData.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    {userData.website}
                  </a>
                ) : '-'}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Shipping Address */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center gap-2 mb-4 pb-2 border-b">
          <MapPin size={20} />
          <h3 className="text-lg font-semibold">Default Shipping Address</h3>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
            {isEditing ? (
              <input
                type="text"
                value={formData.shippingStreet}
                onChange={(e) => setFormData({...formData, shippingStreet: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
              />
            ) : (
              <p className="text-gray-900">{userData?.shippingAddress?.street || '-'}</p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.shippingCity}
                  onChange={(e) => setFormData({...formData, shippingCity: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              ) : (
                <p className="text-gray-900">{userData?.shippingAddress?.city || '-'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.shippingState}
                  onChange={(e) => setFormData({...formData, shippingState: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  maxLength={2}
                />
              ) : (
                <p className="text-gray-900">{userData?.shippingAddress?.state || '-'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.shippingZip}
                  onChange={(e) => setFormData({...formData, shippingZip: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              ) : (
                <p className="text-gray-900">{userData?.shippingAddress?.zip || '-'}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* W9 Upload */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center gap-2 mb-4 pb-2 border-b">
          <FileText size={20} />
          <h3 className="text-lg font-semibold">Tax Documents</h3>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">W9 Form</label>
          
          {userData?.w9Url ? (
            <div className="flex items-center gap-4 mb-4">
              <CheckCircle size={20} className="text-green-500" />
              <div>
                <p className="text-green-600 font-medium">W9 on file</p>
                <a
                  href={userData.w9Url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline"
                >
                  View uploaded W9
                </a>
              </div>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-yellow-800 text-sm">
                ⚠️ W9 form required for wholesale purchases
              </p>
            </div>
          )}

          <div className="mt-4">
            <label className="block">
              <span className="sr-only">Upload W9</span>
              <input
                type="file"
                accept=".pdf"
                onChange={handleW9Upload}
                disabled={uploading}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:text-sm file:font-semibold
                  file:bg-black file:text-white
                  hover:file:bg-gray-800
                  file:cursor-pointer
                  disabled:opacity-50"
              />
            </label>
            <p className="text-xs text-gray-500 mt-2">PDF files only, max 5MB</p>
          </div>

          {uploading && (
            <div className="flex items-center gap-2 mt-4 text-gray-600">
              <Loader2 size={18} className="animate-spin" />
              <span>Uploading...</span>
            </div>
          )}
        </div>
      </div>

      {/* Save/Cancel Buttons */}
      {isEditing && (
        <div className="flex gap-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-black text-white py-3 rounded-lg hover:bg-gray-800 disabled:bg-gray-400 flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={18} />
                Save Changes
              </>
            )}
          </button>
          <button
            onClick={handleCancel}
            disabled={saving}
            className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-2"
          >
            <X size={18} />
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
